import { useLingui } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';

import { BlogPost } from '#features/blog/components/BlogPost';
import { loadBlogPreview } from '#features/blog/server/loadBlogPreview';
import { ContentNotFound } from '#features/error/components/ContentNotFound';
import { Link } from '#features/routing';

/**
 * The admin's look at an unpublished article, rendered by the very page a reader gets.
 *
 * The draft arrives through an httpOnly cookie set by POST /api/blog/preview, so this address
 * carries nothing on its own: opened cold it says the link has expired. No OG tags, no JSON-LD,
 * no canonical, and robots are told to stay away: a preview is not a publication.
 * The loader is server-only by construction (the cookie is path-scoped and httpOnly), so the
 * draft is never fetched from the browser.
 */
export const Route = createFileRoute('/{-$locale}/_layout/blog/preview')({
  loader: async () => ({ post: await loadBlogPreview() }),
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData?.post ? `Draft preview: ${loaderData.post.title}` : 'Draft preview' },
      { name: 'robots', content: 'noindex, nofollow, noarchive' },
      { name: 'referrer', content: 'no-referrer' },
    ],
  }),
  component: BlogPreviewPage,
});

function BlogPreviewPage() {
  const { post } = Route.useLoaderData();
  const { t } = useLingui();

  if (!post) {
    return (
      <ContentNotFound
        title={t`blog.preview.expired.title`}
        description={t`blog.preview.expired.description`}
        action={<Link to="/{-$locale}/blog">{t`notFound.allArticles`}</Link>}
      />
    );
  }

  return <BlogPost post={post} slug={post.slug} preview />;
}
